import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { User, Tokens } from '@meddata/interfaces';
import { AuthHttpService } from './auth-http.service';
import { finalize, tap } from 'rxjs';
import { Role } from '@meddata/constants';
import { jwtDecode } from 'jwt-decode';
import { environment } from '../../../environments/environment';

const TOKEN_KEY = 'token';
const REFRESH_TOKEN_KEY = 'refreshToken';
const USER_KEY = 'user';

@Injectable({
	providedIn: 'root'
})
export class AuthService {
	readonly #token: WritableSignal<string> = signal('');
	readonly #refreshToken: WritableSignal<string> = signal('');
	readonly #user: WritableSignal<User | undefined> = signal(undefined);
	readonly #isTokenUpdating: WritableSignal<boolean> = signal(false);

	/**
	 * A readonly representation of the private `#token`.
	 * This variable provides a secure way to access the token
	 * without allowing modification.
	 *
	 * It is intended to be used where read-only access to the
	 * token is required.
	 */
	readonly token = this.#token.asReadonly();
	/**
	 * Represents a secure, readonly refresh token, typically used for renewing authentication credentials.
	 *
	 * This variable is a wrapper around the private `#refreshToken` field, exposing it as a readonly property.
	 * It ensures the refresh token remains immutable and inaccessible for modifications outside its encapsulating class or module.
	 *
	 * Usage of this variable is essential for maintaining authentication security by safely managing the refresh token lifecycle.
	 */
	readonly refreshToken = this.#refreshToken.asReadonly();
	/**
	 * Represents a user object that is a readonly version of the underlying private user data.
	 * The value of this variable is derived from the private `#user` property and is converted to a readonly format.
	 * This ensures that the consumer of this variable cannot modify the user data directly.
	 */
	readonly user = this.#user.asReadonly();
	/**
	 * A read-only variable that indicates whether the token is currently being updated.
	 * This value reflects the updating state managed internally and is not intended to be modified.
	 */
	readonly isTokenUpdating = this.#isTokenUpdating.asReadonly();

	readonly isLoggedIn: Signal<boolean> = computed(() => !this.isTokenExpired(this.#token()));

	readonly authHttp: AuthHttpService = inject(AuthHttpService);

	updateToken() {
		this.#isTokenUpdating.set(true);
		return this.authHttp.updateToken(this.#refreshToken()).pipe(
			finalize(() => this.#isTokenUpdating.set(false)),
			tap((tokens: Tokens) => this.setTokens(tokens))
		);
	}

	private setUserFromToken() {
		if (!this.#token()) return;
		const decoded: { sub?: User | string } = jwtDecode(this.#token());
		const sub = this.parseTokenSubject(decoded?.sub);
		this.#user.set(this.normalizeUser(sub));
	}

	/**
	 * Updates the token and refresh token values and stores them in local storage.
	 *
	 * @param {Tokens} tokens - An object containing the `token` and `refreshToken` values to be set.
	 * @return {void} This method does not return any value.
	 */
	setTokens(tokens: Tokens): void {
		this.#token.set(tokens.auth_token);
		this.#refreshToken.set(tokens.update_token);
		this.setUserFromToken();
		this.setLocalStorage();
	}

	setUser(user: User | undefined): void {
		this.#user.set(this.normalizeUser(user));
		this.setLocalStorage();
	}

	/**
	 * Stores authentication tokens and user data into the local storage.
	 *
	 * This method saves the following keys in the local storage:
	 * - TOKEN_KEY: Contains the authentication token.
	 * - REFRESH_TOKEN_KEY: Contains the refresh token.
	 * - USER_KEY: Contains the user data as a JSON string.
	 *
	 * @return {void} Does not return any value.
	 */
	private setLocalStorage(): void {
		localStorage.setItem(TOKEN_KEY, this.#token());
		localStorage.setItem(REFRESH_TOKEN_KEY, this.#refreshToken());
		localStorage.setItem(USER_KEY, JSON.stringify(this.#user()));
	}

	/**
	 * Retrieves and sets token, refresh token, and user-related information from the local storage.
	 *
	 * @return {void} This method does not return a value.
	 */

	getInfoFromLocalStorage(): void {
		if (!!localStorage.getItem(TOKEN_KEY)) this.#token.set(localStorage.getItem(TOKEN_KEY) ?? '');
		if (!!localStorage.getItem(REFRESH_TOKEN_KEY))
			this.#refreshToken.set(localStorage.getItem(REFRESH_TOKEN_KEY) ?? '');
		if (!!localStorage.getItem(USER_KEY)) {
			this.#user.set(this.normalizeUser(JSON.parse(localStorage.getItem(USER_KEY) ?? 'null')));
		}
	}

	/**
	 * Checks if the currently authenticated user has the specified role.
	 *
	 * @param {Role} requiredRole - The role to check against the user's assigned role.
	 * @return {boolean} Returns true if the user's role matches the required role, otherwise false.
	 */
	public hasRole(requiredRole: Role): boolean {
		const user = this.#user();
		if (!user || !user.user_company_roles[0].role) return false;
		return user.user_company_roles[0].role === requiredRole;
	}

	isFirstLogin(user: User | undefined = this.#user()): boolean {
		if (!user) return false;
		return Boolean(user.first_login ?? user.firstLogin);
	}

	isProfilingCompleted(user: User | undefined = this.#user()): boolean {
		if (!user) return false;
		return Boolean(user.profilingCompleted);
	}

	isUserActive(user: User | undefined = this.#user()): boolean {
		if (!user) return false;
		return Boolean(user.active);
	}

	/**
	 * Indica se l'utente deve ancora completare la profilazione medica.
	 *
	 * Se la feature profilazione e' disattivata (`environment.profiling === false`)
	 * nessun utente la richiede mai: questo spegne in un colpo solo i redirect a
	 * `/profilazione-medico` da login, primo accesso e guard.
	 */
	requiresProfiling(user: User | undefined = this.#user()): boolean {
		if (!environment.profiling) return false;
		if (!user) return false;
		return !this.isFirstLogin(user) && this.isUserActive(user) && !this.isProfilingCompleted(user);
	}

	/**
	 * Logs the user out by clearing authentication tokens, user information, and relevant data stored in localStorage.
	 *
	 * @return {void} Does not return any value.
	 */
	logout(): void {
		this.#token.set('');
		this.#refreshToken.set('');
		this.#user.set(undefined);
		localStorage.removeItem(TOKEN_KEY);
		localStorage.removeItem(REFRESH_TOKEN_KEY);
		localStorage.removeItem(USER_KEY);
	}

	/**
	 * Indicates whether an authentication token is persisted in localStorage.
	 *
	 * Used to tell apart a first-time/not-yet-authenticated visitor (no stored token)
	 * from a user whose session has actually expired (token stored but no longer valid).
	 *
	 * @return {boolean} True if a session token is present in localStorage.
	 */
	hasStoredSession(): boolean {
		return !!localStorage.getItem(TOKEN_KEY);
	}

	/**
	 * Retrieves the file manager token from the decoded JWT token.
	 * If the token cannot be retrieved or decoded, it returns an empty string.
	 *
	 * @return {string} The file manager token, or an empty string if unavailable.
	 */
	getFileManagerToken(): string {
		try {
			const token = this.#token();
			if (!token) return '';
			const decoded: any = jwtDecode(token);
			return decoded.fmtkn || '';
		} catch {
			return '';
		}
	}

	private isTokenExpired(token: string): boolean {
		if (!token) return true;
		try {
			const { exp }: { exp: number } = jwtDecode(token);
			return new Date().getTime() - new Date(exp * 1000).getTime() > 0;
		} catch {
			return true;
		}
	}

	private parseTokenSubject(sub: User | string | undefined): User | undefined {
		if (!sub) return undefined;
		if (typeof sub === 'string') {
			try {
				return JSON.parse(sub) as User;
			} catch {
				return undefined;
			}
		}
		return sub;
	}

	private normalizeUser(user: User | undefined | null): User | undefined {
		if (!user) return undefined;

		const rawRoles = (user.user_company_roles ??
			user.userCompanyRoles?.map((role) => ({
				company_id: role.companyId,
				role: role.roleType
			})) ??
			[]) as User['user_company_roles'];

		return {
			...user,
			user_company_roles: rawRoles,
			first_login: Boolean(user.first_login ?? user.firstLogin),
			profilingCompleted: Boolean(user.profilingCompleted ?? user.profiling_completed)
		};
	}
}
